import React, { useEffect, useRef, useState } from "react";

interface TagEditableListProps {
  tags: (string | Record<string, any>)[];
  fieldName: string;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  formData: Record<string, any>;
  disabled?: boolean;
  allowTagEdit?: boolean;
}

const TagEditableList: React.FC<TagEditableListProps> = ({
  tags,
  fieldName,
  setFormData,
  formData,
  disabled,
  allowTagEdit = true,
}) => {
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef<any>(null);

  useEffect(() => {
    if (inputRef.current && editingIdx !== null) {
      const len = editValue.length;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(len, len);
    }
  }, [editValue.length > 30, editingIdx]);

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {tags.map((tag, idx) => (
        <span
          key={idx}
          className="bg-blue-100 text-blue-800 px-2 py-1 rounded flex items-center"
        >
          {editingIdx === idx ? (
            <>
              <input
                ref={inputRef}
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="p-1 border border-gray-400 rounded mr-2 w-80"
                disabled={disabled}
                autoFocus
              />
              <div className="flex flex-col gap-1 text-sm">
                <button
                  type="button"
                  className="ml-1 text-green-600 hover:text-green-800"
                  onClick={() => {
                    if (editingIdx === null) return;
                    const trimmed = editValue.trim();
                    if (!trimmed) return;

                    const newTags = tags.map((t, i) => {
                      if (i !== editingIdx) return t;
                      if (typeof t === "object" && t !== null) {
                        return { ...t, title: trimmed };
                      }
                      return trimmed;
                    });

                    setFormData({ ...formData, [fieldName]: newTags });
                    setEditingIdx(null);
                    setEditValue("");
                  }}
                  disabled={disabled || !editValue.trim()}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="ml-1 text-gray-600 hover:text-gray-800"
                  onClick={() => {
                    setEditingIdx(null);
                    setEditValue("");
                  }}
                  disabled={disabled}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              {/* 🩹 Safely render tag whether string or object */}
              {typeof tag === "object" && tag !== null ? (
                <>
                  {tag.title}
                  {tag.amount ? ` ($${tag.amount})` : ""}
                </>
              ) : (
                tag
              )}

              {allowTagEdit && (
                <button
                  type="button"
                  className="ml-2 text-yellow-500 hover:text-yellow-700"
                  onClick={() => {
                    const value =
                      typeof tag === "object" && tag !== null
                        ? tag.title || JSON.stringify(tag)
                        : tag;
                    setEditingIdx(idx);
                    setEditValue(value);
                  }}
                  disabled={disabled}
                >
                  Edit
                </button>
              )}
              <button
                type="button"
                className="ml-2 text-red-500 hover:text-red-700"
                onClick={() =>
                  setFormData({
                    ...formData,
                    [fieldName]: tags.filter((_, i) => i !== idx),
                  })
                }
                disabled={disabled}
              >
                ×
              </button>
            </>
          )}
        </span>
      ))}
    </div>
  );
};

export default TagEditableList;
